import { useState, useEffect, useRef } from 'react'

export const useCarouselAnimation = (photos) => {
  const [isHeld, setIsHeld] = useState(false)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const scrollRef = useRef(null)
  const animationRef = useRef(null)
  const lastFrameTime = useRef(0)

  useEffect(() => {
    if (photos.length > 0 && scrollRef.current) {
      startCarousel()
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [photos, isHeld]) // eslint-disable-line react-hooks/exhaustive-deps

  const startCarousel = () => {
    if (isHeld || !scrollRef.current || !isAutoPlaying) return

    const scrollContainer = scrollRef.current
    const scrollSpeed = 30 // Pixels per second (consistent speed)

    const animate = (currentTime) => {
      if (!isHeld && scrollContainer && isAutoPlaying) {
        // Calculate time delta for consistent speed
        if (lastFrameTime.current === 0) {
          lastFrameTime.current = currentTime
        }

        const deltaTime = currentTime - lastFrameTime.current
        const scrollAmount = (scrollSpeed * deltaTime) / 1000 // Convert to pixels per frame

        scrollContainer.scrollLeft += scrollAmount

        // Reset to beginning when we've scrolled halfway (due to duplication)
        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 2) {
          scrollContainer.scrollLeft = 0
        }

        lastFrameTime.current = currentTime
        animationRef.current = requestAnimationFrame(animate)
      }
    }

    lastFrameTime.current = 0
    animationRef.current = requestAnimationFrame(animate)
  }

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying)
    if (!isAutoPlaying) {
      startCarousel()
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }

  const scrollToDirection = (direction) => {
    if (!scrollRef.current) return

    const scrollContainer = scrollRef.current
    const scrollAmount = 280 // Width of one photo + margin

    if (direction === 'left') {
      scrollContainer.scrollLeft -= scrollAmount
    } else {
      scrollContainer.scrollLeft += scrollAmount
    }
  }

  const pauseAnimation = () => {
    setIsHeld(true)
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }
  }

  const resumeAnimation = () => {
    setIsHeld(false)
    if (isAutoPlaying) {
      startCarousel()
    }
  }

  return {
    scrollRef,
    isAutoPlaying,
    toggleAutoPlay,
    scrollToDirection,
    pauseAnimation,
    resumeAnimation
  }
}